import { useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { QuickScoreEntry } from '@/components/game-entry/QuickScoreEntry'
import { DetailedGoalEntryV2 } from '@/components/game-entry/DetailedGoalEntryV2'
import type { GameType } from '@foosball/shared'

type GameMode = 'team' | 'cross_team' | 'free_play'

export function CreateGame() {
  const [gameMode, setGameMode] = useState<GameMode>('team')
  const [gameType, setGameType] = useState<GameType>('2v2')
  const [targetScore, setTargetScore] = useState<number>(10)
  const [entryMode, setEntryMode] = useState<'quick' | 'detailed' | null>(null)
  const [step, setStep] = useState<'setup' | 'record'>('setup')

  const handleContinue = () => {
    if (entryMode) {
      setStep('record')
    }
  }

  const handleBack = () => {
    setStep('setup')
    setEntryMode(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        {step === 'record' ? (
          <button
            onClick={handleBack}
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Setup
          </button>
        ) : (
          <Link
            to="/games"
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Games
          </Link>
        )}
      </div>

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Record New Game</h1>
        <p className="mt-1 text-sm text-gray-500">
          {step === 'setup' ? 'Choose your game settings' : 'Record the game details'}
        </p>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {step === 'setup' ? (
            <div className="space-y-6">
              {/* Game mode selection */}
              <div>
                <label className="text-base font-medium text-gray-900">Game Mode</label>
                <p className="text-sm leading-5 text-gray-500">
                  Choose where players are from
                </p>
                <fieldset className="mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setGameMode('team')}
                      className={`p-3 rounded-md border text-left ${
                        gameMode === 'team'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">Within Team</div>
                      <div className="text-sm opacity-80">Players from your team</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setGameMode('cross_team')}
                      className={`p-3 rounded-md border text-left ${
                        gameMode === 'cross_team'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">Cross-Team</div>
                      <div className="text-sm opacity-80">Team vs Team match</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setGameMode('free_play')}
                      className={`p-3 rounded-md border text-left ${
                        gameMode === 'free_play'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">Free Play</div>
                      <div className="text-sm opacity-80">Mix players from any team</div>
                    </button>
                  </div>
                </fieldset>
              </div>

              {/* Game type selection */}
              <div>
                <label className="text-base font-medium text-gray-900">Game Type</label>
                <p className="text-sm leading-5 text-gray-500">
                  Choose the game format
                </p>
                <fieldset className="mt-4">
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        id="2v2"
                        name="game-type"
                        type="radio"
                        value="2v2"
                        checked={gameType === '2v2'}
                        onChange={(e) => setGameType(e.target.value as GameType)}
                        className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                      />
                      <label htmlFor="2v2" className="ml-3 block text-sm font-medium text-gray-700">
                        2v2 (Team vs Team)
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="1v1"
                        name="game-type"
                        type="radio"
                        value="1v1"
                        checked={gameType === '1v1'}
                        onChange={(e) => setGameType(e.target.value as GameType)}
                        className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                      />
                      <label htmlFor="1v1" className="ml-3 block text-sm font-medium text-gray-700">
                        1v1 (Full Table)
                      </label>
                    </div>
                  </div>
                </fieldset>
              </div>

              {/* Target score */}
              <div>
                <label className="text-base font-medium text-gray-900">Target Score</label>
                <p className="text-sm leading-5 text-gray-500">First team to reach this score wins</p>
                <div className="mt-4 flex items-center gap-2">
                  {([5, 10] as number[]).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setTargetScore(v)}
                      className={`px-3 py-1.5 rounded-md border ${
                        targetScore === v
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                  <div className="flex items-center gap-2 ml-2">
                    <span className="text-sm text-gray-600">Custom</span>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      value={targetScore}
                      onChange={(e) => setTargetScore(parseInt(e.target.value || '0'))}
                      className="w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Entry mode selection */}
              <div>
                <label className="text-base font-medium text-gray-900">Entry Mode</label>
                <p className="text-sm leading-5 text-gray-500">
                  Choose how detailed you want to record the game
                </p>
                <fieldset className="mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setEntryMode('quick')}
                      className={`p-3 rounded-md border text-left ${
                        entryMode === 'quick'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">Quick Entry</div>
                      <div className="text-sm opacity-80">Just final scores</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEntryMode('detailed')}
                      className={`p-3 rounded-md border text-left ${
                        entryMode === 'detailed'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">Detailed Entry</div>
                      <div className="text-sm opacity-80">Goal by goal recording</div>
                    </button>
                  </div>
                </fieldset>
              </div>

              <div className="pt-5">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleContinue}
                    disabled={!entryMode}
                    className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {entryMode === 'quick' && (
                <QuickScoreEntry gameType={gameType} targetScore={targetScore} gameMode={gameMode} />
              )}
              {entryMode === 'detailed' && (
                <DetailedGoalEntryV2 gameType={gameType} targetScore={targetScore} gameMode={gameMode} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
